import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";

const STARTER_PROMPTS = [
  "Change the outfit colour",
  "Fix the hair on the back",
  "Make the back match the front",
  "The side view looks too flat",
];

export const DuckHelper = ({ creation, applyOptions = [], applyBusy, onApply, emptyApplyHint, starters = STARTER_PROMPTS }) => {
  const [messages, setMessages] = useState([]);
  const [instruction, setInstruction] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [tailored, setTailored] = useState(null); // duck-specific chips fetched lazily; null = not yet fetched
  const scrollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/creations/${creation.id}/helper`)
      .then(({ data }) => {
        if (!alive) return;
        setMessages(data.messages || []);
        setInstruction(data.instruction || "");
        if ((data.messages || []).length) setOpen(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [creation.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Fetch chips tailored to this duck's outfit/hair the first time the helper is opened.
  const startersFor = useRef(null);
  useEffect(() => {
    if (!open || startersFor.current === creation.id) return;
    startersFor.current = creation.id;
    api
      .get(`/creations/${creation.id}/helper/starters`)
      .then(({ data }) => {
        if (Array.isArray(data.starters) && data.starters.length) setTailored(data.starters);
      })
      .catch(() => {
        startersFor.current = null;
      });
  }, [open, creation.id]);

  const chips = tailored && tailored.length ? tailored : starters;

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInstruction(text); // show the sculptor's own request as the change to apply right away; refined by the reply
    setInput("");
    try {
      const { data } = await api.post(`/creations/${creation.id}/helper`, { message: text });
      setMessages(data.messages || []);
      setInstruction(data.instruction || text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The duck helper could not reply — please try again.");
      setMessages((m) => m.filter((x, i) => !(i === m.length - 1 && x.role === "user")));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete(`/creations/${creation.id}/helper`);
      setMessages([]);
      setInstruction("");
      toast("Cleared the helper chat");
    } catch {
      toast.error("Could not clear the chat");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div data-testid="duck-helper" className="rounded-3xl border-2 border-accent/40 bg-accent/5 overflow-hidden">
      <button
        data-testid="duck-helper-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <Sparkles className="h-4 w-4 text-accent shrink-0" />
        <span className="font-heading font-bold text-sm">Ask the Duck Helper</span>
        <span className="text-xs text-muted-foreground ml-auto">{open ? "Hide" : "Not sure what to change? Tap to chat"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div ref={scrollRef} data-testid="duck-helper-messages" className="max-h-64 overflow-y-auto space-y-2 pr-1">
            {messages.length === 0 && !sending && (
              <p className="text-xs text-muted-foreground bg-card rounded-2xl p-3">
                Hi! Tell me what looks off on the back or side views — e.g. "the jacket should be navy, not black" — and I'll
                turn it into a clear note you can redo with.
              </p>
            )}
            {messages.length === 0 && !sending && starters.length > 0 && (
              <div data-testid="duck-helper-starters" className="flex flex-wrap gap-2 pt-1">
                {chips.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`duck-helper-starter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    onClick={() => send(s)}
                    className="rounded-full border border-accent/50 bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={`${i}-${m.role}-${(m.content || "").slice(0, 24)}`}
                data-testid={`duck-helper-msg-${m.role}`}
                className={`text-sm rounded-2xl px-3 py-2 max-w-[90%] ${
                  m.role === "user" ? "ml-auto bg-accent text-accent-foreground" : "bg-card border border-border"
                }`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="bg-card border border-border rounded-2xl px-3 py-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> thinking…
              </div>
            )}
          </div>

          {instruction && (
            <div data-testid="duck-helper-instruction" className="rounded-2xl border border-accent bg-card p-3 space-y-2">
              <p className="text-[11px] font-mono2 uppercase tracking-widest text-accent flex items-center gap-1.5">
                <Wand2 className="h-3.5 w-3.5" /> Change to apply
              </p>
              <p className="text-sm">{instruction}</p>
              {applyOptions.length > 0 ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  {applyOptions.map((opt) => (
                    <Button
                      key={opt.id}
                      data-testid={`duck-helper-apply-${opt.id}`}
                      size="sm"
                      disabled={!!applyBusy}
                      onClick={() => onApply(opt.id, instruction)}
                      className="rounded-full h-8 gap-1.5 text-xs font-bold"
                    >
                      {applyBusy === opt.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                      {opt.label}
                    </Button>
                  ))}
                </div>
              ) : (
                emptyApplyHint && <p className="text-xs text-muted-foreground">{emptyApplyHint}</p>
              )}
            </div>
          )}

          <div className="flex items-end gap-2">
            <Textarea
              data-testid="duck-helper-input"
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 1000))}
              onKeyDown={onKeyDown}
              placeholder="Describe what you'd like changed…"
              rows={2}
              disabled={sending}
              className="text-sm resize-none rounded-2xl bg-background"
            />
            <div className="flex flex-col gap-1.5">
              <Button data-testid="duck-helper-send" onClick={send} disabled={sending || !input.trim()} className="rounded-full h-10 w-10 p-0">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
              {messages.length > 0 && (
                <Button data-testid="duck-helper-clear" variant="ghost" onClick={clear} disabled={sending} className="rounded-full h-10 w-10 p-0 text-muted-foreground">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
